import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { AddMigrationQueueRequest } from 'core/application/csiDashboard/migration/addMigrationQueue'

import Option from 'common/components/dataEntry/components/Option'
import { DatePicker, Select } from 'common/components/dataEntry/formik'
import TextArea from 'common/components/dataEntry/formik/FormikDefaultTextAreaField'
import Modal from 'common/components/feedback/Modal'

import { Button, Col, Row } from '../../common/components'

interface MigrationQueueFormProps {
  visible: boolean
  submitting: boolean
  onCancel: (e: React.MouseEvent<HTMLElement>) => void
  onSubmit: (values: AddMigrationQueueRequest) => void | Promise<any>
}

const MigrationQueueForm: React.FC<MigrationQueueFormProps> = ({ visible, submitting, onCancel, onSubmit }) => {
  let { t: translate } = useTranslation()
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [addMigrationQueueRequest, setAddMigrationQueueRequest] = useState<AddMigrationQueueRequest>(
    {} as AddMigrationQueueRequest,
  )

  const formFooter = (
    <div>
      <Button className={'mr-8'} onClick={onCancel}>
        {translate('general.cancel')}
      </Button>

      <Button
        type="primary"
        loading={submitting}
        disabled={!formIsValid}
        onClick={() => {
          onSubmit(addMigrationQueueRequest)
        }}
      >
        {translate('general.save')}
      </Button>
    </div>
  )

  const formRef = useRef(null)

  return (
    <Modal
      onCancel={onCancel}
      visible={visible}
      title={translate('csi-dashboard.migration.add-new-migration')}
      footer={formFooter}
    >
      <Formik
        enableReinitialize={true}
        onSubmit={(values) => {}}
        initialValues={{}}
        validationSchema={formValidator(translate)}
        validate={(values: any) => {
          setFormIsValid(formValidator(translate).isValidSync(values))

          if (values.companyIds && values.scheduleDate) {
            const companyIds = values.companyIds.trim().split('\n')

            const scheduleDate = new Date(values.scheduleDate)
            scheduleDate.setUTCHours(values.hour ?? 0)
            scheduleDate.setUTCMinutes(values.minute ?? 0)

            setAddMigrationQueueRequest({
              companyIds: companyIds,
              scheduleDate: scheduleDate,
            } as AddMigrationQueueRequest)
          }

          return {}
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} autoComplete="off" ref={formRef}>
            <Row gutter={16}>
              <Col span={8}>
                <DatePicker
                  optional={false}
                  name="scheduleDate"
                  placeholder="Schedule Date"
                  id="scheduleDate"
                ></DatePicker>
              </Col>

              <Col span={8}>
                <Select name="hour" placeholder="Hour">
                  <Option key={0} value={0}>
                    00
                  </Option>
                  <Option key={1} value={1}>
                    01
                  </Option>
                  <Option key={2} value={2}>
                    02
                  </Option>
                  <Option key={3} value={3}>
                    03
                  </Option>
                  <Option key={4} value={4}>
                    04
                  </Option>
                  <Option key={5} value={5}>
                    05
                  </Option>
                  <Option key={6} value={6}>
                    06
                  </Option>
                  <Option key={7} value={7}>
                    07
                  </Option>
                  <Option key={8} value={8}>
                    08
                  </Option>
                  <Option key={9} value={9}>
                    09
                  </Option>
                  <Option key={10} value={10}>
                    10
                  </Option>
                  <Option key={11} value={11}>
                    11
                  </Option>
                  <Option key={12} value={12}>
                    12
                  </Option>
                  <Option key={13} value={13}>
                    13
                  </Option>
                  <Option key={14} value={14}>
                    14
                  </Option>
                  <Option key={15} value={15}>
                    15
                  </Option>
                  <Option key={16} value={16}>
                    16
                  </Option>
                  <Option key={17} value={17}>
                    17
                  </Option>
                  <Option key={18} value={18}>
                    18
                  </Option>
                  <Option key={19} value={19}>
                    19
                  </Option>
                  <Option key={20} value={20}>
                    20
                  </Option>
                  <Option key={21} value={21}>
                    21
                  </Option>
                  <Option key={22} value={22}>
                    22
                  </Option>
                  <Option key={23} value={23}>
                    23
                  </Option>
                </Select>
              </Col>
              <Col span={8}>
                <Select name="minute" placeholder="Minute">
                  <Option key={0} value={0}>
                    00
                  </Option>
                  <Option key={15} value={15}>
                    15
                  </Option>
                  <Option key={30} value={30}>
                    30
                  </Option>
                  <Option key={45} value={45}>
                    45
                  </Option>
                </Select>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <TextArea rows={10} name="companyIds"></TextArea>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </Modal>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    companyIds: Yup.string().nullable().trim().required(translate('general.required')),
    scheduleDate: Yup.date().required(translate('general.required')),
    hour: Yup.number().required(translate('general.required')),
    minute: Yup.number().required(translate('general.required')),
  })

export default MigrationQueueForm
